import {Divider, Skeleton, Stack} from '@mui/material';
import {CarePlanActivityTypesArrangement} from '../../../backend/enums/care-plan-activity-types-arrangement';

interface ActivityTypeEntrySkeletonProps {
  layoutType: CarePlanActivityTypesArrangement;
}

const ColumnsLayoutSkeleton = () => (
  <Stack
    sx={{
      height: '92px',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}
  >
    <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={5} height="100%" />
    <Stack
      gap={2}
      flexDirection="row"
      justifyContent="center"
      alignItems="center"
      sx={{marginLeft: '8px'}}
    >
      <Stack gap="8px" justifyContent="center">
        <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={60} height={15} />
        <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={30} height={10} />
      </Stack>
    </Stack>
  </Stack>
);

const LinesLayoutSkeleton = () => (
  <Stack
    sx={{
      height: '124px',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      overflow: 'auto',
    }}
  >
    <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={5} height="100%" />
    <Stack
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: '12px',
        marginLeft: '8px',
      }}
    >
      <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={60} height={15} />
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '20px',
        }}
      >
        <Stack gap="8px" justifyContent="center">
          <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={120} height={15} />
          <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={30} height={10} />
        </Stack>
        <Stack gap="8px" justifyContent="center">
          <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={120} height={15} />
          <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={30} height={10} />
        </Stack>
        <Stack gap="8px" justifyContent="center">
          <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={120} height={15} />
          <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={300} height={10} />
        </Stack>
        <Stack gap="8px" justifyContent="center">
          <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={120} height={15} />
          <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={300} height={10} />
        </Stack>
        <Stack gap="8px" justifyContent="center">
          <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={120} height={15} />
          <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={300} height={10} />
        </Stack>
      </Stack>
    </Stack>
  </Stack>
);

const MedicationLayoutSkeleton = () => (
  <Stack
    sx={{
      height: '152px',
      display: 'flex',
      flexGrow: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}
  >
    <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={5} height="100%" />
    <Stack
      sx={{
        display: 'flex',
        flexGrow: 1,
        flexDirection: 'column',
        gap: '18px',
        marginX: '12px',
        paddingY: '16px',
      }}
    >
      <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={450} />
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '58px',
          marginY: '10px',
        }}
      >
        <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={480} height={15} />
        <Stack
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '28px',
          }}
        >
          <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={100} height={15} />
          <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={180} height={15} />
          <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={100} height={15} />
        </Stack>
      </Stack>
      <Divider />
      <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={450} height={15} />
    </Stack>
  </Stack>
);

const MedicalAppointmentLayoutSkeleton = () => (
  <Stack
    sx={{
      height: '152px',
      display: 'flex',
      flexGrow: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}
  >
    <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={5} height="100%" />
    <Stack
      sx={{
        display: 'flex',
        flexGrow: 1,
        flexDirection: 'column',
        gap: '18px',
        marginX: '12px',
        paddingY: '16px',
      }}
    >
      <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={450} />
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '58px',
          marginY: '10px',
        }}
      >
        <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={480} height={15} />
        <Stack
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '28px',
          }}
        >
          <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={100} height={15} />
          <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={180} height={15} />
        </Stack>
      </Stack>
      <Divider />
      <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={450} height={15} />
    </Stack>
  </Stack>
);

export const ActivityTypeEntrySkeleton = ({layoutType}: ActivityTypeEntrySkeletonProps) => {
  const SkeletonComponent = () => {
    switch (layoutType) {
      case CarePlanActivityTypesArrangement.Columns:
        return <ColumnsLayoutSkeleton />;
      case CarePlanActivityTypesArrangement.Lines:
        return <LinesLayoutSkeleton />;
      case CarePlanActivityTypesArrangement.Medication:
        return <MedicationLayoutSkeleton />;
      case CarePlanActivityTypesArrangement.MedicalAppointment:
        return <MedicalAppointmentLayoutSkeleton />;
      default:
        return null;
    }
  };

  return <SkeletonComponent />;
};
